import { Template } from '@dev/ee-api/ee-api-models';
import { EditorSaveData, EditorTemplateStatus } from '@dev/template-editor';
import { AvailableFeaturesUpselling } from '@dev/ee-modules';
import { ApplicationState } from '..';
import {
    TemplateEditorActionTypes,
    SendTestAction,
    SetSendTestDataAction,
    SendTestData,
    CloseAction,
    UploadEditorFileAction,
    UploadEditorFileStartAction,
    EditorFile,
    UploadEditorFileSuccessAction,
    UploadEditorFileErrorAction,
    FilesType,
    SetBodyPartAction,
    SetDefaultOptionsAction,
    DefaultOptions,
    AddTagAction,
    RemoveTagAction,
    SaveStartAction,
    SaveErrorAction,
    SaveSuccessAction,
    SetDefaultTemplateAction,
    LoadStartAction,
    LoadSuccessAction,
    LoadErrorAction,
    TemplateEditorState,
    ResetAction,
    IncrementAutosaveCounterAction,
    TemplatePreviewType,
    SetTemplatePreviewHtmlAction,
    OpenUpsellingOverlayAction,
    CloseUpsellingOverlayAction,
} from './types';

export const Close = (): CloseAction => ({
    type: TemplateEditorActionTypes.CLOSE,
});

export const SendTest = (): SendTestAction => ({
    type: TemplateEditorActionTypes.SEND_TEST,
});

export const SetSendTestData = (data: SendTestData): SetSendTestDataAction => ({
    type: TemplateEditorActionTypes.SET_SEND_TEST_DATA,
    payload: data,
});

export const UploadEditorFile = (file: File, blockId: string): UploadEditorFileAction => ({
    type: TemplateEditorActionTypes.UPLOAD_FILE,
    payload: {
        file,
        blockId,
    },
});

export const UploadEditorFileStart = (): UploadEditorFileStartAction => ({
    type: TemplateEditorActionTypes.UPLOAD_FILE_START,
});

export const UploadEditorFileSuccess = (filesList: EditorFile[]): UploadEditorFileSuccessAction => ({
    type: TemplateEditorActionTypes.UPLOAD_FILE_SUCCESS,
    payload: {
        filesList,
    },
});

export const UploadEditorFileError = (): UploadEditorFileErrorAction => ({
    type: TemplateEditorActionTypes.UPLOAD_FILE_ERROR,
});

export const SetBodyPart = (contentType: string, content: string): SetBodyPartAction => ({
    type: TemplateEditorActionTypes.SET_BODY_PART,
    payload: {
        contentType,
        content,
    },
});

export const SetDefaultOptions = (defaultOptions: DefaultOptions): SetDefaultOptionsAction => ({
    type: TemplateEditorActionTypes.SET_DEFAULT_OPTIONS,
    payload: defaultOptions,
});

export const AddTag = (tag: string): AddTagAction => ({
    type: TemplateEditorActionTypes.ADD_TAG,
    payload: { tag },
});

export const RemoveTag = (tag: string): RemoveTagAction => ({
    type: TemplateEditorActionTypes.REMOVE_TAG,
    payload: { tag },
});

export const SetDefaultTemplate = ({ templateObject }: { templateObject: Template }): SetDefaultTemplateAction => ({
    type: TemplateEditorActionTypes.SET_DEFAULT_TEMPLATE,
    payload: { templateObject },
});

export const LoadStart = (name: string, isPublic: boolean, useDraftScope: boolean): LoadStartAction => ({
    type: TemplateEditorActionTypes.LOAD_START,
    payload: { name, isPublic, useDraftScope },
});

export const LoadSuccess = (
    templateObject: Template,
    isPublic: boolean,
    useDraftScope: boolean
): LoadSuccessAction => ({
    type: TemplateEditorActionTypes.LOAD_SUCCESS,
    payload: { templateObject, isPublic, useDraftScope },
});

export const LoadError = (): LoadErrorAction => ({
    type: TemplateEditorActionTypes.LOAD_ERROR,
});

export const IncrementAutosaveCounter = (): IncrementAutosaveCounterAction => ({
    type: TemplateEditorActionTypes.INCREMENT_AUTOSAVE_COUNTER,
});

export const SaveStart = ({
    isDraftTemplate,
    useIndividualPaths,
    data,
    exitOnSave,
    onSaveAndExitCallback,
}: {
    isDraftTemplate: boolean;
    useIndividualPaths: boolean;
    data: EditorSaveData;
    exitOnSave: boolean;
    onSaveAndExitCallback?: () => void;
}): SaveStartAction => ({
    type: TemplateEditorActionTypes.SAVE_START,
    payload: { isDraftTemplate, data, exitOnSave, useIndividualPaths, onSaveAndExitCallback },
});

export const SaveSuccess = (): SaveSuccessAction => ({
    type: TemplateEditorActionTypes.SAVE_SUCCESS,
});

export const SaveError = (nameError?): SaveErrorAction => ({
    type: TemplateEditorActionTypes.SAVE_ERROR,
    payload: { nameError },
});

export const Reset = (): ResetAction => ({
    type: TemplateEditorActionTypes.RESET,
});

export const getEditorTemplateStore = (store: ApplicationState): TemplateEditorState => store.templateEditor;

export const getEditorTemplateStatusStore = (store: ApplicationState): EditorTemplateStatus =>
    store.templateEditor.status;

export const getEditorTemplateLoadedJsonStore = (store: ApplicationState): Record<any, any> =>
    store.templateEditor.templateLoadedJson;

export const SetTemplatePreviewHtml = (templateHtml: string): SetTemplatePreviewHtmlAction => ({
    type: TemplateEditorActionTypes.SET_TEMPLATE_PREVIEW_HTML,
    payload: {
        templateHtml,
    },
});

export const OpenUpsellingOverlay = (feature: AvailableFeaturesUpselling): OpenUpsellingOverlayAction => ({
    type: TemplateEditorActionTypes.OPEN_UPSELLING_OVERLAY,
    payload: {
        feature,
    },
});

export const CloseUpsellingOverlay = (): CloseUpsellingOverlayAction => ({
    type: TemplateEditorActionTypes.CLOSE_UPSELLING_OVERLAY,
});

export const getEditorTemplateObjectStore = (store: ApplicationState): Template => store.templateEditor.templateObject;

export const getEditorFilesStore = (store: ApplicationState): FilesType => store.templateEditor.files;

export const getEditorTemplatePreviewStore = (store: ApplicationState): TemplatePreviewType =>
    store.templateEditor.templatePreview;
